import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService } from '@cosmos/angular/utils';
import { AdminModuleSummary, BillingConfig, ModuleAccessMap, ModuleKey, ProjectsModuleVisibilityMap } from '@cosmos/shared';
import { catchError, map, Observable } from 'rxjs';
import { environment } from '../../../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class BillingConfigService {

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
  ) {}

  find(query = ''): Observable<HttpResponse<BillingConfig[]>> {
    return this.http
      .get<BillingConfig[]>(`${environment.API_URL}/billing-configs${query}`, { observe: 'response' })
      .pipe(
        map(r => r),
        catchError(e => { this.errorService.handleError(e); throw e; })
      );
  }

  findById(id: string, query = ''): Observable<HttpResponse<BillingConfig>> {
    return this.http
      .get<BillingConfig>(`${environment.API_URL}/billing-configs/${id}${query}`, { observe: 'response' })
      .pipe(
        map(r => r),
        catchError(e => { this.errorService.handleError(e); throw e; })
      );
  }

  create(body: Partial<BillingConfig>): Observable<HttpResponse<BillingConfig>> {
    return this.http
      .post<BillingConfig>(`${environment.API_URL}/billing-configs`, body, { observe: 'response' })
      .pipe(
        map(r => r),
        catchError(e => { this.errorService.handleError(e); throw e; })
      );
  }

  update(id: string, body: Partial<BillingConfig>): Observable<HttpResponse<BillingConfig>> {
    return this.http
      .patch<BillingConfig>(`${environment.API_URL}/billing-configs/${id}`, body, { observe: 'response' })
      .pipe(
        map(r => r),
        catchError(e => { this.errorService.handleError(e); throw e; })
      );
  }

  delete(id: string): Observable<HttpResponse<any>> {
    return this.http
      .delete(`${environment.API_URL}/billing-configs/${id}`, { observe: 'response' })
      .pipe(
        map(r => r),
        catchError(e => { this.errorService.handleError(e); throw e; })
      );
  }

  getUsage(id: string, start: string, end: string): Observable<HttpResponse<any>> {
    return this.http
      .get(`${environment.API_URL}/billing-configs/${id}/usage?start=${start}&end=${end}`, { observe: 'response' })
      .pipe(
        map(r => r),
        catchError(e => { this.errorService.handleError(e); throw e; })
      );
  }

  updateQuota(id: string, dto: { metric: string; delta: number; type: 'REFILL' | 'CORRECTION' | 'INITIAL'; lane?: 'paidBalance' | 'freeAllowance'; reason?: string }): Observable<HttpResponse<BillingConfig>> {
    return this.http
      .post<BillingConfig>(`${environment.API_URL}/billing-configs/${id}/quota`, dto, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  updateBucketFlags(id: string, dto: { metric: string; isBillable?: boolean; allowNegative?: boolean }): Observable<HttpResponse<BillingConfig>> {
    return this.http
      .patch<BillingConfig>(`${environment.API_URL}/billing-configs/${id}/bucket-flags`, dto, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  provisionBucket(id: string, dto: { module: ModuleKey; metric: string; overuseLimit: number; resetTarget: number | null; isBillable?: boolean; allowNegative?: boolean }): Observable<HttpResponse<BillingConfig>> {
    return this.http
      .post<BillingConfig>(`${environment.API_URL}/billing-configs/${id}/provision-bucket`, dto, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  topUpBucket(id: string, dto: { metric: string; amount: number; reason?: string }): Observable<HttpResponse<BillingConfig>> {
    // legacy redirection to new updateQuota endpoint
    return this.updateQuota(id, { metric: dto.metric, delta: dto.amount, type: 'REFILL', reason: dto.reason });
  }

  findByProject(projectId: string): Observable<HttpResponse<BillingConfig | null>> {
    return this.http
      .get<BillingConfig[]>(`${environment.API_URL}/billing-configs?project=${projectId}&limit=1`, { observe: 'response' })
      .pipe(
        map(r => r.clone({ body: r.body?.[0] ?? null })),
        catchError(e => { this.errorService.handleError(e); throw e; })
      );
  }

  getModuleAccess(projectId: string): Observable<ModuleAccessMap> {
    return this.http
      .get<ModuleAccessMap>(`${environment.API_URL}/billing-configs/module-access?project=${projectId}`)
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  getProjectsModuleVisibility(projectIds: string[]): Observable<ProjectsModuleVisibilityMap> {
    const ids = projectIds.join(',');
    return this.http
      .get<ProjectsModuleVisibilityMap>(`${environment.API_URL}/billing-configs/projects-module-visibility?projects=${ids}`)
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  getAdminModuleSummary(): Observable<AdminModuleSummary> {
    return this.http
      .get<AdminModuleSummary>(`${environment.API_URL}/billing-configs/admin-module-summary`)
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }
}
